import {
  Box,
  Button,
  Dialog as ChakraDialog,
  DialogBodyProps,
  DialogContentProps,
  DialogFooterProps,
  DialogHeaderProps,
  DialogPositionerProps,
  DialogRootProps,
  Portal,
  useDialogContext,
} from '@chakra-ui/react'
import {useHotkey} from '@tanstack/react-hotkeys'
import {LuX} from 'react-icons/lu'

export interface DialogProps extends DialogRootProps {
  contentProps?: DialogContentProps
  positionerProps?: DialogPositionerProps
  portalled?: boolean
  hideCloseButton?: boolean
}

export function Dialog({
  children,
  contentProps,
  positionerProps,
  portalled = true,
  hideCloseButton,
  ...props
}: DialogProps) {
  const internalProps: Partial<DialogRootProps> = {
    size: 'lg',
    placement: 'center',
  }
  if (props.id) internalProps.id = props.id

  return (
    <ChakraDialog.Root {...internalProps} {...props}>
      <Portal disabled={!portalled}>
        <ChakraDialog.Backdrop />

        <ChakraDialog.Positioner py="4" {...positionerProps}>
          <ChakraDialog.Content
            maxHeight="100%"
            height="max-content"
            {...contentProps}
            css={{'& > form': {overflowY: 'auto', display: 'flex', flexDirection: 'column'}}}
          >
            <SubmitForm />

            {children}

            {!hideCloseButton && (
              <ChakraDialog.CloseTrigger asChild>
                <Button variant="plain" size="md" position="absolute" right="2" top="4">
                  <LuX />
                </Button>
              </ChakraDialog.CloseTrigger>
            )}
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </Portal>
    </ChakraDialog.Root>
  )
}

interface HeaderProps extends DialogHeaderProps {
  description?: string
}

export function DialogHeader({children, description, ...props}: HeaderProps) {
  return (
    <ChakraDialog.Header {...props}>
      <Box>
        <ChakraDialog.Title>{children}</ChakraDialog.Title>
        {description && (
          <ChakraDialog.Description fontSize="sm" color="fg.muted">
            {description}
          </ChakraDialog.Description>
        )}
      </Box>
    </ChakraDialog.Header>
  )
}

export function DialogBody({children, ...props}: DialogBodyProps) {
  return (
    <ChakraDialog.Body
      height="100%"
      // minHeight="calc(100dvh - 10rem)" // 2rem -> margin top and bottom from dialog to screen; 8rem -> dialog header & footer
      overflow="hidden"
      display="grid" // to easily fill the body with the children
      transition="none"
      {...props}
    >
      {children}
    </ChakraDialog.Body>
  )
}

export function DialogFooter({children, ...props}: DialogFooterProps) {
  return <ChakraDialog.Footer {...props}>{children}</ChakraDialog.Footer>
}

function SubmitForm() {
  const context = useDialogContext()

  useHotkey(
    'Mod+Enter',
    () => {
      const id = context.getContentProps().id
      if (!id) return

      const dialogEl = document.getElementById(id)
      if (!dialogEl) return

      const form = dialogEl.querySelector('form')
      if (!form) return

      form.requestSubmit()
    },
    {conflictBehavior: 'replace', ignoreInputs: false, stopPropagation: true, preventDefault: true}
  )

  return null
}
